<template>
	<v-badge
		v-model="hasBusiness"
		bordered
		bottom
		right
		:offset-x="size * 0.25"
		:offset-y="size * -0.07"
		class="badge-container"
	>
		<template #badge>
			<v-icon>mdi-domain</v-icon>
		</template>

		<v-sheet v-if="sentiment && !isAnonymousOrEmergencyNumber" class="position-absolute above emoji-wrapper">
			<sentiment-emoji :size="size / 4" :sentiment="sentiment" class="antialias" />
		</v-sheet>
		<v-tooltip :disabled="!goToConversation" top>
			<template #activator="{ on, attrs }">
				<v-avatar
					:color="color"
					:size="size"
					v-bind="attrs"
					:class="{ 'avatar-btn': goToConversation }"
					v-on="on"
					@click="showConversation"
				>
					<v-icon v-if="isAnonymousOrEmergencyNumber" color="white" :size="size * 0.50">
						{{ isAnonymous ? "mdi-incognito" : "mdi-car-emergency" }}
					</v-icon>

					<span v-else-if="initials" class="white--text font-weight-bold" :style="{ fontSize: (size / 3) + 'px' }">
						{{ initials }}
					</span>

					<v-icon v-else color="white" :size="size * 0.75">
						{{ "mdi-account" }}
					</v-icon>
				</v-avatar>
			</template>
			<span>{{ $t("show-conversation") }}</span>
		</v-tooltip>
	</v-badge>
</template>

<script>
import { fullName, initialsGenerator } from "@/common/utils";
import { IS_ANONYMOUS, IS_EMERGENCY } from "@/common/const";
import SentimentEmoji, { Sentiment } from "../conversations/contact-card/SentimentEmoji.vue";
import { mapActions } from "vuex";
import { bus } from "@/main.js";

export default {
	name: "ContactAvatar",
	components: { SentimentEmoji },

	props: {
		size: {
			type: Number,
			default: 50
		},

		contact: {
			/*
			 * contact object is
			 * {
			 *   phone: String,
			 *   name: Object, in case of user
			 *   fullname: String,
			 *   businessName: String
			 * }
			 */
			type: Object,
			default: undefined
		},

		sentimentScore: {
			type: Number,
			default: undefined
		},

		goToConversation: {
			type: Boolean,
			default: false
		},

		avatarColor: {
			type: String,
			default: ""
		}
	},

	computed: {
		initials() {
			if (this.avatarName) {
				return initialsGenerator(this.avatarName);
			}
			return undefined;
		},

		color() {
			return this.avatarColor || (this.avatarName?.length ? "initials" : "avatar");
		},

		hasBusiness() {
			return (this.contact?.businessName ?? "") !== "";
		},

		sentiment() {
			if (this.sentimentScore >= 70) return Sentiment.Positive;
			if (this.sentimentScore <= 40) return Sentiment.Negative;

			return undefined;
		},

		isAnonymous() {
			return IS_ANONYMOUS(this.phoneNumber);
		},

		isEmergency() {
			return IS_EMERGENCY(this.phoneNumber);
		},

		avatarName() {
			// Resolve a display name from any of: fullname, name, or firstname/lastname.
			// fullName() handles all shapes so initials work for raw contact records
			// (e.g. family members fetched without a pre-built fullname).
			const resolved = fullName(this.contact);

			return resolved || undefined;
		},

		phoneNumber() {
			return this.contact?.phone;
		},

		isAnonymousOrEmergencyNumber() {
			return (this.isAnonymous || this.isEmergency);
		}
	},

	methods: {
		...mapActions("conversation", ["displayConversation", "findByPhoneNumber"]),

		async showConversation() {
			if (this.goToConversation && this.contact?.phone) {
				const sid = await this.findByPhoneNumber(this.contact?.phone);

				if (sid) {
					bus.$emit("show-conversation", sid);
				}
			}
		}
	}
};
</script>
<style>
	.badge-container .v-badge__wrapper{
		position: relative;
	}

	.conversation-button {
		position: absolute;
		top: -5px;
		right: -0px;
		background-color: white;
		border-radius: 50%;
		z-index: 1;
	}

	.avatar-btn {
		cursor: pointer;
	}

	.emoji-wrapper{
		background-color: transparent !important;
	}
</style>